import { v4 as uuidv4 } from 'uuid';

// The IVR / call chatbot is a strict tree. Each node is one of:
//   - menu             : plays `audio`, waits for a digit, routes to a child option
//   - end              : plays `audio` then hangs up
//   - transfer_agent   : routes to the AI agent (optional agent_id)
//   - transfer_human   : routes to a live agent (optional agent_id)
// Children of a menu node are its digit options; each child carries the digit
// it is mapped to in `optionKey` and a display `label`.

export const NODE_KINDS = {
	MENU: 'menu',
	END: 'end',
	TRANSFER_AGENT: 'transfer_agent',
	TRANSFER_HUMAN: 'transfer_human'
};

export const NODE_KIND_OPTIONS = [
	{ value: NODE_KINDS.MENU, label: 'Menu (with options)' },
	{ value: NODE_KINDS.END, label: 'Audio then End' },
	{ value: NODE_KINDS.TRANSFER_AGENT, label: 'Go to AI Agent' },
	{ value: NODE_KINDS.TRANSFER_HUMAN, label: 'Go to Human' }
];

export const DEFAULT_NO_REPLY_TIMEOUT_MS = 5000;
export const DEFAULT_NO_REPLY_MAX_ATTEMPTS = 3;
export const DEFAULT_ON_END = 'disconnect';

const firstUrl = audio => (audio && audio.length && audio[0] ? audio[0].URL || '' : '');
const wrapAudio = url => (url ? [{ URL: url, caption: '' }] : []);

// agent_id can be a numeric id (live agent, e.g. 42) or an opaque string id
// (AI agent, e.g. an ElevenLabs agent id). Keep numbers numeric, strings as-is.
const normalizeAgentId = value => {
	if (value == null || value === '') return undefined;
	const str = String(value).trim();
	if (str === '') return undefined;
	return /^\d+$/.test(str) ? Number(str) : str;
};

// Build the default root menu node for a brand new call chatbot.
export const createDefaultTree = () => [
	{
		id: uuidv4(),
		title: 'Welcome',
		nodeKind: NODE_KINDS.MENU,
		expanded: true,
		audio: [],
		noReplyAudio: [],
		invalidAudio: [],
		noReplyTimeoutMs: DEFAULT_NO_REPLY_TIMEOUT_MS,
		noReplyMaxAttempts: DEFAULT_NO_REPLY_MAX_ATTEMPTS,
		children: []
	}
];

// Duplicate a tree node, giving the node (and, when `withChildren`, every
// descendant) a fresh id so react-sortable-tree keeps stable keys.
//   - withChildren = true  -> deep copy (the node and all its sub-options),
//                             used to clone a whole language menu at once.
//   - withChildren = false -> copy just this node with no sub-options
//                             ("Copy Parent").
export const cloneTreeNode = (node, withChildren = true) => {
	const cloned = { ...node, id: uuidv4(), expanded: true };
	if (withChildren && node.children && node.children.length) {
		cloned.children = node.children.map(child => cloneTreeNode(child, true));
	} else {
		cloned.children = [];
	}
	return cloned;
};

// Convert a single tree node into its IVR payload shape.
// `isRoot` controls whether the `label` field is included (the root has none)
// and whether the `on_end` directive is emitted.
export const nodeToIvr = (node, isRoot = false) => {
	const kind = node.nodeKind || NODE_KINDS.MENU;

	if (kind === NODE_KINDS.TRANSFER_AGENT) {
		// AI agent transfer carries the selected provider as `agent_type`. When the
		// provider is ElevenLabs a specific agent is chosen too, so `agent_id` holds
		// that agent's id; other providers (e.g. OpenAI/ChatGPT) have no agents, so
		// `agent_id` is null.
		const result = { action: kind };
		if (!isRoot) result.label = node.label || node.title || '';
		if (node.agentType) result.agent_type = node.agentType;
		const agentId = normalizeAgentId(node.agentId);
		result.agent_id = agentId !== undefined ? agentId : null;
		return result;
	}

	if (kind === NODE_KINDS.TRANSFER_HUMAN) {
		// Live agent transfer routes to an optional numeric live agent id and
		// supports an optional audio prompt plus optional hold music.
		const result = { action: kind };
		if (!isRoot) result.label = node.label || node.title || '';
		const agentId = normalizeAgentId(node.agentId);
		if (agentId !== undefined) result.agent_id = agentId;
		const audioUrl = firstUrl(node.audio);
		if (audioUrl) result.audio = audioUrl;
		const holdMusicUrl = firstUrl(node.holdMusic);
		if (holdMusicUrl) result.hold_music = holdMusicUrl;
		return result;
	}

	if (kind === NODE_KINDS.END) {
		const result = { audio: firstUrl(node.audio), action: 'end' };
		if (!isRoot) result.label = node.label || node.title || '';
		return result;
	}

	// menu
	const options = {};
	let disabledCount = 0;
	(node.children || []).forEach(child => {
		const rawKey = child.optionKey != null && child.optionKey !== '' ? String(child.optionKey) : '';
		// A node with no key is still kept in the payload (so neither it nor its
		// sub-tree is lost) but under a non-digit placeholder key. The caller can
		// never dial it, so it exists but is effectively disabled — this is also
		// what a freshly copied/cloned node looks like until a key is assigned.
		const key = rawKey !== '' ? rawKey : `disabled_${disabledCount++}`;
		options[key] = nodeToIvr(child, false);
	});

	const menu = {
		audio: firstUrl(node.audio),
		no_reply_audio: firstUrl(node.noReplyAudio),
		invalid_audio: firstUrl(node.invalidAudio),
		no_reply_timeout_ms: Number(node.noReplyTimeoutMs) || DEFAULT_NO_REPLY_TIMEOUT_MS,
		no_reply_max_attempts: Number(node.noReplyMaxAttempts) || DEFAULT_NO_REPLY_MAX_ATTEMPTS,
		options
	};
	if (isRoot) menu.on_end = node.onEnd || DEFAULT_ON_END;
	else menu.label = node.label || node.title || '';
	return menu;
};

// Convert a saved IVR payload back into a tree node (for editing).
export const ivrToNode = (menu, optionKey, label) => {
	const base = { id: uuidv4(), expanded: true };

	if (!menu || typeof menu !== 'object') {
		return {
			...base,
			title: label || 'Menu',
			label: label || '',
			optionKey,
			nodeKind: NODE_KINDS.MENU,
			audio: [],
			noReplyAudio: [],
			invalidAudio: [],
			noReplyTimeoutMs: DEFAULT_NO_REPLY_TIMEOUT_MS,
			noReplyMaxAttempts: DEFAULT_NO_REPLY_MAX_ATTEMPTS,
			children: []
		};
	}

	if (menu.action === NODE_KINDS.TRANSFER_AGENT || menu.action === NODE_KINDS.TRANSFER_HUMAN) {
		return {
			...base,
			title: menu.label || label || '',
			label: menu.label || label || '',
			optionKey,
			nodeKind: menu.action,
			agentType: menu.agent_type != null ? String(menu.agent_type) : '',
			agentId: menu.agent_id != null ? String(menu.agent_id) : '',
			audio: wrapAudio(menu.audio),
			holdMusic: wrapAudio(menu.hold_music),
			children: []
		};
	}

	if (menu.action === 'end') {
		return {
			...base,
			title: menu.label || label || '',
			label: menu.label || label || '',
			optionKey,
			nodeKind: NODE_KINDS.END,
			audio: wrapAudio(menu.audio),
			children: []
		};
	}

	const children = Object.entries(menu.options || {}).map(([key, value]) => {
		// Only real digit keys map back to an option key; placeholder keys used to
		// persist keyless (disabled) nodes come back as a blank key.
		const optionKey = /^[0-9]+$/.test(key) ? key : '';
		return ivrToNode(value, optionKey, value && value.label);
	});

	return {
		...base,
		title: menu.label || label || 'Welcome',
		label: menu.label || label || '',
		optionKey,
		nodeKind: NODE_KINDS.MENU,
		audio: wrapAudio(menu.audio),
		noReplyAudio: wrapAudio(menu.no_reply_audio),
		invalidAudio: wrapAudio(menu.invalid_audio),
		noReplyTimeoutMs: menu.no_reply_timeout_ms || DEFAULT_NO_REPLY_TIMEOUT_MS,
		noReplyMaxAttempts: menu.no_reply_max_attempts || DEFAULT_NO_REPLY_MAX_ATTEMPTS,
		onEnd: menu.on_end || DEFAULT_ON_END,
		children
	};
};

// Validate a tree before saving. Returns an array of human readable errors.
export const validateTree = rootNode => {
	const errors = [];
	if (!rootNode) {
		errors.push('Add at least one menu node.');
		return errors;
	}

	const walk = (node, isRoot) => {
		const kind = node.nodeKind || NODE_KINDS.MENU;
		const labelForMsg = isRoot ? 'Welcome node' : `Key "${node.optionKey}" (${node.label || node.title || ''})`;

		// The key (option digit) is optional: a node left with a blank key stays in
		// the flow but is not reachable (it is dropped from the API payload). Only
		// non-empty keys must be unique among siblings.
		if (kind === NODE_KINDS.MENU) {
			if (!firstUrl(node.audio)) errors.push(`${labelForMsg}: audio is required.`);
			const keys = (node.children || [])
				.map(c => (c.optionKey == null ? '' : String(c.optionKey)))
				.filter(k => k !== '');
			const dupes = keys.filter((k, i) => keys.indexOf(k) !== i);
			if (dupes.length) errors.push(`${labelForMsg}: duplicate keys (${[...new Set(dupes)].join(', ')}).`);
			(node.children || []).forEach(child => walk(child, false));
		} else if (kind === NODE_KINDS.END) {
			if (!firstUrl(node.audio)) errors.push(`${labelForMsg}: audio is required.`);
		}
	};

	walk(rootNode, true);
	return errors;
};
